import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { uploadFile, UploadOptions, UploadResult, UploadStatus } from '../api/upload'
import { queryKeys } from '../lib/query-client'

interface UseUploadReturn {
  upload: (options: Omit<UploadOptions, 'onProgress'>) => Promise<UploadResult>
  progress: number
  status: UploadStatus
  error: Error | null
  reset: () => void
  isUploading: boolean
  isComplete: boolean
  isError: boolean
}

/**
 * Hook for file upload with progress tracking.
 *
 * Manages upload state (idle, uploading, processing, complete, error)
 * and tracks upload progress percentage.
 *
 * @returns Upload mutation, progress, status, and helper functions
 */
export function useUpload(): UseUploadReturn {
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState<UploadStatus>('idle')
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (options: Omit<UploadOptions, 'onProgress'>) => {
      setStatus('uploading')
      setProgress(0)

      const result = await uploadFile({
        ...options,
        onProgress: (p) => setProgress(p),
      })

      // After upload completes, status moves to 'processing'
      // The actual processing status comes from SSE
      setStatus('processing')
      return result
    },
    onSuccess: (data) => {
      setStatus('complete')
      // Invalidate runs list to show new upload
      queryClient.invalidateQueries({
        queryKey: queryKeys.runs.lists(),
      })
      return data
    },
    onError: (error) => {
      setStatus('error')
      throw error
    },
  })

  const upload = useCallback(
    (options: Omit<UploadOptions, 'onProgress'>) => {
      return mutation.mutateAsync(options)
    },
    [mutation]
  )

  const reset = useCallback(() => {
    setProgress(0)
    setStatus('idle')
    mutation.reset()
  }, [mutation])

  return {
    upload,
    progress,
    status,
    error: mutation.error as Error | null,
    reset,
    isUploading: status === 'uploading',
    isComplete: status === 'complete',
    isError: status === 'error',
  }
}
